import { useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { Loader2, Wand2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";
import { useCreateBoard, CreateBoardPersona } from "@/hooks/useCreateBoard";
import { supabase } from "@/integrations/supabase/client";

type PrebuiltPersona = {
  role_name: string;
  mission: string;
  principles: string;
  competencies: string;
  key_relationships: string;
  typical_questions: string[];
};

type PrebuiltBoard = {
  id: string;
  name: string;
  description: string;
  personas: PrebuiltPersona[];
};

interface PrebuiltBoardSelectorProps {
  onSuccess?: (boardId: string) => void;
  onRemix?: (board: PrebuiltBoard) => void;
}

const PrebuiltBoardSelector = ({ onSuccess, onRemix }: PrebuiltBoardSelectorProps = {}) => {
  const { createBoard, isCreating } = useCreateBoard();
  const [selectedBoard, setSelectedBoard] = useState<string | null>(null);
  const [boardName, setBoardName] = useState("");

  const { data: prebuiltBoards = [], isLoading: loading } = useQuery<PrebuiltBoard[]>({
    queryKey: ['prebuilt_boards'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("prebuilt_boards")
        .select("*")
        .eq("status", "published")
        .order("created_at", { ascending: true });

      if (error) throw error;
      
      return (data || []).map(board => ({
        ...board,
        personas: board.personas as unknown as PrebuiltPersona[]
      }));
    },
  });

  const handleSelectBoard = (boardId: string) => {
    const board = prebuiltBoards.find(b => b.id === boardId);
    setSelectedBoard(boardId);
    setBoardName(board?.name || "");
  };

  const handleCreateBoard = async () => {
    if (!boardName.trim() || !selectedBoard) {
      toast.error("Please enter a board name");
      return;
    }

    const selectedBoardData = prebuiltBoards.find(b => b.id === selectedBoard);
    if (!selectedBoardData) return;

    await createBoard({
      boardName,
      description: selectedBoardData.description,
      personas: selectedBoardData.personas as CreateBoardPersona[],
      boardType: "prebuilt",
      templateBoardId: selectedBoard,
      onSuccess
    });
  };

  const selectedBoardData = prebuiltBoards.find(b => b.id === selectedBoard);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h1 className="text-3xl font-semibold">Choose a Pre-built Board</h1>
        <p className="text-muted-foreground">
          Select a battle-tested advisory board template
        </p>
      </div>

      {!selectedBoard ? (
        <div className="grid gap-4 md:grid-cols-3">
          {prebuiltBoards.map((board) => (
            <Card 
              key={board.id}
              className="cursor-pointer hover:shadow-md transition-all hover:border-primary"
              onClick={() => handleSelectBoard(board.id)}
            >
              <CardHeader>
                <CardTitle>{board.name}</CardTitle>
                <CardDescription>{board.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Includes {board.personas.length} expert personas
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>{selectedBoardData?.name}</CardTitle>
              <CardDescription>{selectedBoardData?.description}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <p className="font-medium">Included Experts:</p>
                <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                  {selectedBoardData?.personas.map((persona, idx) => (
                    <li key={idx}>{persona.role_name}</li>
                  ))}
                </ul>
              </div>
            </CardContent>
          </Card>

          <div className="space-y-4">
            <div>
              <Label htmlFor="boardName">Name Your Board</Label>
              <Input
                id="boardName"
                value={boardName}
                onChange={(e) => setBoardName(e.target.value)}
                placeholder="e.g., My Knowledge Product Board"
              />
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setSelectedBoard(null)}>
                Back
              </Button>
              
              {onRemix && selectedBoardData && (
                <Button 
                  variant="secondary" 
                  onClick={() => onRemix(selectedBoardData)}
                  className="gap-2"
                >
                  <Wand2 className="h-4 w-4" />
                  Remix with AI
                </Button>
              )}

              <Button 
                onClick={handleCreateBoard} 
                disabled={isCreating}
                className="flex-1"
              >
                {isCreating ? "Creating..." : "Create Board"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrebuiltBoardSelector;
